import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import { getWebSocketUrl } from '../config/api';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  // Store WebSocket connections by rfqNumber
  const connectionsRef = useRef({});
  // Store status of each connection
  const [connectionStatus, setConnectionStatus] = useState({});
  // Store message handlers by rfqNumber - multiple components can subscribe
  const handlersRef = useRef({});

  // Get or create a WebSocket connection for an RFQ
  const getConnection = useCallback((rfqNumber) => {
    if (!rfqNumber) return null;
    
    // Return existing connection if available and open
    if (connectionsRef.current[rfqNumber]) {
      const ws = connectionsRef.current[rfqNumber];
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        return ws;
      }
      // Connection is closed or closing, remove it
      delete connectionsRef.current[rfqNumber];
    }

    // Create new connection
    const endpoint = `/api/chat/${rfqNumber}`;
    const ws = new WebSocket(getWebSocketUrl(endpoint));
    
    setConnectionStatus(prev => ({ ...prev, [rfqNumber]: 'connecting' }));
    
    ws.onopen = () => {
      console.log(`[WebSocket] Connected to ${rfqNumber}`);
      setConnectionStatus(prev => ({ ...prev, [rfqNumber]: 'open' }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Notify all registered handlers for this RFQ
        const handlers = handlersRef.current[rfqNumber] || [];
        handlers.forEach(handler => handler(data));
      } catch (err) {
        console.error(`[WebSocket] Parse error for ${rfqNumber}:`, err, event.data);
      }
    };

    ws.onerror = (error) => {
      console.error(`[WebSocket] Error for ${rfqNumber}:`, error);
      setConnectionStatus(prev => ({ ...prev, [rfqNumber]: 'error' }));
    };

    ws.onclose = () => {
      console.log(`[WebSocket] Disconnected from ${rfqNumber}`);
      setConnectionStatus(prev => ({ ...prev, [rfqNumber]: 'closed' }));
      delete connectionsRef.current[rfqNumber];
    };

    connectionsRef.current[rfqNumber] = ws;
    return ws;
  }, []);

  // Subscribe to messages for an RFQ
  const subscribe = useCallback((rfqNumber, handler) => {
    if (!rfqNumber || !handler) return () => {};
    
    // Add handler to list
    if (!handlersRef.current[rfqNumber]) {
      handlersRef.current[rfqNumber] = [];
    }
    handlersRef.current[rfqNumber].push(handler);
    
    // Ensure connection exists
    getConnection(rfqNumber);
    
    // Return unsubscribe function
    return () => {
      const handlers = handlersRef.current[rfqNumber];
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
        // If no more handlers, we can optionally close the connection
        // For now, keep it open for performance
      }
    };
  }, [getConnection]);

  // Send a message to an RFQ
  const sendMessage = useCallback((rfqNumber, message) => {
    const ws = getConnection(rfqNumber);
    if (!ws) {
      console.warn(`[WebSocket] No connection for ${rfqNumber}`);
      return false;
    }
    
    if (ws.readyState !== WebSocket.OPEN) {
      console.warn(`[WebSocket] Connection not open for ${rfqNumber}`);
      return false;
    }
    
    try {
      ws.send(JSON.stringify(message));
      return true;
    } catch (err) {
      console.error(`[WebSocket] Failed to send message to ${rfqNumber}:`, err);
      return false;
    }
  }, [getConnection]);

  // Close a specific connection
  const closeConnection = useCallback((rfqNumber) => {
    const ws = connectionsRef.current[rfqNumber];
    if (ws) {
      try {
        ws.close();
      } catch (err) {
        console.error(`[WebSocket] Error closing connection for ${rfqNumber}:`, err);
      }
      delete connectionsRef.current[rfqNumber];
      delete handlersRef.current[rfqNumber];
      setConnectionStatus(prev => {
        const next = { ...prev };
        delete next[rfqNumber];
        return next;
      });
    }
  }, []);

  const value = {
    subscribe,
    sendMessage,
    getConnection,
    closeConnection,
    connectionStatus,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
